import React, { ReactNode } from "react";

import "./style.scss";

export interface Props {
    /** Any custom className(s) to be added to the boxes default classes */
    className?: string;
    children: ReactNode;
    /** Will render the box with a alternative background color (grey) */
    alternateBackground?: boolean;
    /**
     * Set this to true, if the box should not utilize a bottom padding.
     * Useful when nesting boxes and want to avoid double spacing.
     */
    noBottomPadding?: boolean;
    /**
     * Enable this, to have the box rendered as `<section>` element instead of a regular `<div>`.
     */
    section?: boolean;

    /** The component will pass any additional props down to the DIV */
    [x: string]: any;
}

/**
 * This layout component provides a box with 24px padding and optional alternating background color.
 *
 * Enable the `section` flag to render the box as `<section>` instead of `<div>`.
 */
export const Box = ({
    className,
    children,
    noBottomPadding = false,
    alternateBackground = false,
    section,
    ...rest
}: Props) => {
    const classNames = ["box"];

    if (alternateBackground) {
        classNames.push("dark");
    }

    if (noBottomPadding) {
        classNames.push("noBottomSpace");
    }

    if (className) {
        classNames.push(className);
    }

    return React.createElement(section ? "section" : "div", { ...rest, className: classNames.join(" ") }, children);
};

export function withBox<PROPS = any>(Component: React.ElementType<any>, isSection?: boolean): React.FC<PROPS>{
    return (props: PROPS) => (
        <Box section={isSection}>
            <Component {...props} />
        </Box>
    );
}

export function withSectionBox<PROPS = any>(Component: React.ElementType<any>): React.FC<PROPS>{
    return withBox(Component, true);
}
